import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import MiniGraph from './MinigraphPage';
import PumpGraph from './PumpgraphPage';
import { 
  AlertTriangle, 
  Power, 
  Activity, 
  Settings,
  Bell,
  Sun,
  Moon
} from 'lucide-react';

import './DashboardPage.css';
import { dashboardService } from '../../services/dashboardService';

// --- Mock Data Generators ---
const generateWaveData = (points = 20, offset = 0) => {
  return Array.from({ length: points }, (_, i) => ({
    time: i,
    value: 50 + 30 * Math.sin((i + offset) * 0.5) + Math.random() * 10
  }));
};

const generatePumpData = (points = 20) => {
  return Array.from({ length: points }, (_, i) => ({
    time: i,
    status: Math.random() > 0.5 ? 1 : 0 
  }));
};

// --- Leaflet Map Component ---
const LeafletMap = ({ isDarkMode, markers = [] }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markersLayerRef = useRef(null); // NEW: Reference to hold the group of markers
  const [isLoaded, setIsLoaded] = useState(false);

  // 1. Initialize Map Shell (Runs Once)
  useEffect(() => {
    let isMounted = true;

    const loadLeaflet = () => {
      // Check for CSS
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      // Check for JS
      if (!window.L) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        script.onload = () => {
          if (isMounted) initMap();
        };
        document.head.appendChild(script);
      } else {
        initMap();
      }
    };

    const initMap = () => {
      if (!isMounted) return;
      if (mapInstanceRef.current) return;
      if (!mapContainerRef.current) return;
      if (!window.L) return;

      try {
        // Create Map Instance
        const map = window.L.map(mapContainerRef.current).setView([2.18, 102.25], 12);
        mapInstanceRef.current = map;

        // Add Tile Layer
        tileLayerRef.current = window.L.tileLayer('', {
          attribution: '',
          maxZoom: 19
        }).addTo(map);

        // NEW: Create a LayerGroup to hold markers and add it to map
        // This allows us to clear just the markers later without reloading the whole map
        markersLayerRef.current = window.L.layerGroup().addTo(map);

        setIsLoaded(true);
        
        setTimeout(() => {
          if (isMounted && map) map.invalidateSize();
        }, 200);

      } catch (error) {
        console.error("Map initialization failed:", error);
      }
    };

    loadLeaflet();

    return () => {
      isMounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // 2. Handle Dark Mode (Runs when theme changes)
  useEffect(() => {
    if (tileLayerRef.current) {
      const darkUrl = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
      const lightUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
      tileLayerRef.current.setUrl(isDarkMode ? darkUrl : lightUrl);
    }
  }, [isDarkMode, isLoaded]);

  // 3. Handle Markers (Runs when 'markers' prop changes)
  useEffect(() => {
    // Only run if map is loaded and we have the layer group ready
    if (!isLoaded || !markersLayerRef.current || !window.L) return;

    // A. Clear existing markers
    markersLayerRef.current.clearLayers();

    // B. Helper to create icon
    const createCustomIcon = (color, pulse = false) => {
      return window.L.divIcon({
        className: 'custom-div-icon',
        html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 10px ${color};" class="${pulse ? 'marker-pulse' : ''}"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      });
    };

    // C. Loop through data and add new markers
    markers.forEach(item => {
      // Default color logic if none provided
      const color = item.color || '#3b82f6'; 
      const pulse = item.status === 'active' || item.status === 'warning';

      const marker = window.L.marker([item.lat, item.lng], { 
        icon: createCustomIcon(color, pulse) 
      });

      // Bind Popup with dynamic content
      marker.bindPopup(`
        <div class="font-sans">
          <h3 class="font-bold text-sm mb-1">${item.name}</h3>
          <p class="text-xs text-gray-600 dark:text-gray-300">
            Type: ${item.type}<br/>
            Status: ${item.status}
          </p>
        </div>
      `);

      // Add to the LayerGroup (not directly to map)
      marker.addTo(markersLayerRef.current);
    });

  }, [markers, isLoaded]); // This effect triggers whenever 'markers' data updates

  return (
    <div className="relative w-full h-full min-h-[300px]">
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-slate-900 text-gray-400 dark:text-slate-500 text-xs">
          Loading Map Interface...
        </div>
      )}
      <div ref={mapContainerRef} className="w-full h-full min-h-[300px] z-0" />
    </div>
  );
};

// --- Reusable Components ---

const Card = ({ title, children, className = "" }) => (
  <div className={`bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden flex flex-col shadow-lg ${className}`}>
    <div className="bg-gray-50/50 dark:bg-slate-900/50 px-4 py-2 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
      <h3 className="text-gray-600 dark:text-slate-300 font-semibold text-sm uppercase tracking-wider flex items-center gap-2">
        {title}
      </h3>
      <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>
    </div>
    <div className="p-4 flex-1 relative min-h-0">
      {children}
    </div>
  </div>
);

const StatusBox = ({ label, isActive, type = "default" }) => {
  const activeColor = type === 'danger' ? 'bg-red-500 shadow-red-500/50' : 'bg-emerald-500 shadow-emerald-500/50';
  const inactiveColor = 'bg-gray-200 dark:bg-slate-700';

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4 flex flex-col items-center justify-center gap-2 shadow-md transition-all hover:border-gray-300 dark:hover:border-slate-600">
      <span className="text-gray-500 dark:text-slate-400 text-xs font-bold uppercase tracking-widest">{label}</span>
      <div className={`px-4 py-1 rounded font-bold text-lg transition-all duration-300 shadow-lg ${isActive ? `text-white ${activeColor}` : `text-gray-800 dark:text-white ${inactiveColor}`}`}>
        {isActive ? 'ON' : 'OFF'}
      </div>
    </div>
  );
};

const TriggerButton = ({ label, isActive, onClick }) => (
  <button 
    onClick={onClick}
    className={`group relative overflow-hidden rounded-lg p-4 border transition-all duration-200 flex flex-col items-center gap-2
      ${isActive 
        ? 'bg-emerald-50/50 dark:bg-emerald-900/30 border-emerald-500/50 hover:bg-emerald-100/50 dark:hover:bg-emerald-900/50' 
        : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-100 dark:hover:bg-slate-700'}
    `}
  >
    <div className={`p-3 rounded-full transition-all duration-300 ${isActive ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/50' : 'bg-gray-200 dark:bg-slate-900 text-gray-400 dark:text-slate-500'}`}>
      <Power size={24} />
    </div>
    <span className={`text-xs font-bold uppercase tracking-wider ${isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-slate-400'}`}>
      {label}
    </span>
  </button>
);

// const MiniGraph = ({ data, color, type = "line" }) => (
//   <ResponsiveContainer width="100%" height="100%">
//     {type === 'step' ? (
//       <LineChart data={data}>
//         <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="dark:stroke-slate-700" vertical={false} />
//         <YAxis hide domain={[0, 1.2]} />
//         <Line 
//           type="step" 
//           dataKey="status" 
//           stroke={color} 
//           strokeWidth={2} 
//           dot={false} 
//           isAnimationActive={false} 
//         />
//       </LineChart>
//     ) : (
//       <AreaChart data={data}>
//         <defs>
//           <linearGradient id={`color${color}`} x1="0" y1="0" x2="0" y2="1">
//             <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
//             <stop offset="95%" stopColor={color} stopOpacity={0}/>
//           </linearGradient>
//         </defs>
//         <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="dark:stroke-slate-700" vertical={false} />
//         <YAxis hide domain={[0, 100]} />
//         <Area 
//           type="monotone" 
//           dataKey="value" 
//           stroke={color} 
//           strokeWidth={2} 
//           fillOpacity={1} 
//           fill={`url(#color${color})`} 
//           isAnimationActive={false}
//         />
//       </AreaChart>
//     )}
//   </ResponsiveContainer>
// );

const ThemeToggle = ({ isDarkMode, setIsDarkMode }) => {
  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  return (
    <button onClick={toggleTheme} className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full transition-colors">
      {isDarkMode ? <Sun size={20} className="text-slate-200" /> : <Moon size={20} className="text-gray-800" />}
    </button>
  );
};

// --- Main App Component ---

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [isDarkMode, setIsDarkMode] = useState(true);
  // map state added
  const [mapMarkers, setMapMarkers] = useState([]);
  // State for simulated live data
  const [pump1On, setPump1On] = useState(true);
  const [pump2On, setPump2On] = useState(false);
  const [mainPower, setMainPower] = useState(true);
  const [elkbStatus, setElkbStatus] = useState(true);
  
  // Data State
  // const [pump1Data, setPump1Data] = useState(generatePumpData());
  // const [pump2Data, setPump2Data] = useState(generatePumpData());
  // const [wl1Data, setWl1Data] = useState(generateWaveData(20, 0));
  // const [wl2Data, setWl2Data] = useState(generateWaveData(20, 10));
  // const [wl3Data, setWl3Data] = useState(generateWaveData(20, 20));
  // const [pump1Data, setPump1Data] = useState([]);
  // const [pump2Data, setPump2Data] = useState([]);
  const [pump1HouseStatus, setPump1Status] = useState(null);
  const [pump2HouseStatus, setPump2Status] = useState(null);
  const [wl1Data, setWl1Data] = useState([]);
  const [wl2Data, setWl2Data] = useState([]);
  const [wl3Data, setWl3Data] = useState([]);
  // Alert State
  const [alerts] = useState([
    { id: 1, msg: "ELKB Trip Warning", time: "10:23 AM", severity: "high" },
    { id: 2, msg: "WL 2 Over 3 Meters", time: "10:15 AM", severity: "medium" },
    { id: 3, msg: "WL 1 Over 2 Meters", time: "09:45 AM", severity: "low" },
  ]);

  const [showMenu, setShowMenu] = useState(false);
  const isAdmin = user && user.user.roles.includes('Super Admin');

  const handleUserManagement = () => {
    // Navigate to user management page or open a modal
    console.log('Navigating to User Management');
    setShowMenu(false);
  };
  const fetchDashboardData = async () => {
    try {
      // 1. Get IO Board Status (PH1)
      const status = await dashboardService.getPumpHouseStatus();
      
      // Update Switches/Indicators based on GPIO mapping
      // Assuming mapping: Input1=Pump1Status, Input3=MainPower, Input4=ELKB
      setPump1On(status.pump1Active); 
      setPump2On(status.pump2Active);
      setMainPower(status.mainPowerStatus === 'on'); // Assuming 'on' string from payload
      setElkbStatus(status.elkbStatus === 'on');
      setPump1Status(status.pump1Activity);
      setPump2Status(status.pump2Activity);

      // Update Pump Activity Graphs (Append new data point)
      // const now = Date.now();
      // setPump1Data(prev => [...prev.slice(1), { time: now, status: status.pump1Active ? 1 : 0 }]);
      // setPump2Data(prev => [...prev.slice(1), { time: now, status: status.pump2Active ? 1 : 0 }]);

      // 2. Get Water Levels (Full 6H history, replacing array)
      const wl1 = await dashboardService.getWaterLevelHistory('PH1_WL1');
      const wl2 = await dashboardService.getWaterLevelHistory('PH1_WL2');
      const wl3 = await dashboardService.getWaterLevelHistory('PH1_WL3');

      // Transform backend data to Recharts format
      // Backend should return { timestamp, value }
      setWl1Data(wl1); 
      setWl2Data(wl2);
      setWl3Data(wl3);

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    const loadMap = async () => {
      try {
        const locations = await dashboardService.getMapLocations();
        console.log('Fetched map locations:', locations);
        setMapMarkers(locations);
      } catch (e) { console.error(e); }
    };
    loadMap();
  }, []);


  // 4. Polling Interval (e.g., every 5 seconds)
  useEffect(() => {
    fetchDashboardData(); // Initial load
    const interval = setInterval(fetchDashboardData, 60000); // Poll every 15s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Simulation Tick
  // useEffect(() => {
  //   const interval = setInterval(() => {
      
  //     // Update graph data (shift and push)
  //     const updateData = (prev, isPump = false) => {
  //       const newVal = isPump 
  //         ? (Math.random() > 0.8 ? (Math.random() > 0.5 ? 1 : 0) : prev[prev.length - 1].status) 
  //         : 50 + 30 * Math.sin((Date.now() / 1000) + Math.random()) + Math.random() * 10;
        
  //       const newPoint = isPump 
  //         ? { time: Date.now(), status: newVal } 
  //         : { time: Date.now(), value: newVal };
          
  //       return [...prev.slice(1), newPoint];
  //     };

  //     setPump1Data(p => updateData(p, true));
  //     setPump2Data(p => updateData(p, true));
  //     setWl1Data(p => updateData(p));
  //     setWl2Data(p => updateData(p));
  //     setWl3Data(p => updateData(p));

  //   }, 1000);
  //   return () => clearInterval(interval);
  // }, []);

  const togglePump1 = () => setPump1On(!pump1On);
  const togglePump2 = () => setPump2On(!pump2On);  

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-slate-950 text-gray-800 dark:text-slate-200 font-sans p-4 md:p-6 flex flex-col gap-6">
      
      {/* Header */}
      <header className="flex justify-between items-center bg-white/50 dark:bg-slate-900/50 p-4 rounded-xl border border-gray-200 dark:border-slate-800 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="bg-blue-600 p-2 rounded-lg">
            <Activity className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">Main Dashboard</h1>
            <p className="text-gray-500 dark:text-slate-400 text-xs">System Status: Nominal</p>
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <span className="text-gray-500 dark:text-slate-400 text-sm font-mono">{new Date().toLocaleTimeString()}</span>
          <button className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors relative">
            <Bell size={20} />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
          </button>
          <ThemeToggle isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full transition-colors">
              <Settings size={20} />
            </button>
            {showMenu && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md shadow-lg z-10">
                <div className="py-1">
                  {isAdmin && <button onClick={handleUserManagement} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700">User Management</button>}
                  <button onClick={logout} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700">Logout</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 flex-1">
        
        {/* Left Column: Map & Alerts (Span 5) */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          
          {/* Map Section - NOW USING REAL MAP */}
          <Card title="MONITORING MAP LOCATION" className="flex-grow min-h-[300px] overflow-hidden">
             <LeafletMap isDarkMode={isDarkMode} markers={mapMarkers} />
          </Card>

          {/* Alert Messages (Bottom Left in sketch) */}
          <Card title="System Alerts" className="h-64">
            <div className="flex flex-col gap-2 overflow-y-auto h-full pr-2 custom-scrollbar">
              {alerts.map((alert) => (
                <div key={alert.id} className={`
                  p-3 rounded border-l-4 flex items-start gap-3 bg-gray-50/50 dark:bg-slate-900/50
                  ${alert.severity === 'high' ? 'border-red-500' : alert.severity === 'medium' ? 'border-amber-500' : 'border-blue-500'}
                `}>
                  <AlertTriangle size={18} className={alert.severity === 'high' ? 'text-red-500' : 'text-gray-400 dark:text-slate-400'} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800 dark:text-slate-200">{alert.msg}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">{alert.time}</p>
                  </div>
                </div>
              ))}
              <div className="p-2 text-center text-xs text-gray-400 dark:text-slate-600 mt-2">End of logs</div>
            </div>
          </Card>
        </div>

        {/* Right Column: Graphs & Controls (Span 7) */}
        <div className="lg:col-span-7 grid grid-rows-[auto_auto_auto] gap-6">
          
          {/* Row 1: Pump Graphs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-48">
          <Card title="Pump 1 Activity">
            <div className="h-full w-full pt-2">
              <PumpGraph
                data={pump1HouseStatus}
                color="#3b82f6"
                type="step"
              />
            </div>
          </Card>

          <Card title="Pump 2 Activity">
            <div className="h-full w-full pt-2">
              <PumpGraph
                data={pump2HouseStatus}
                color="#8b5cf6"
                type="step"
              />
            </div>
          </Card>
          </div>

          {/* Row 2: Status Switches */}
          <div className="grid grid-cols-2 gap-6">
            <StatusBox label="ELCB 13A" isActive={elkbStatus} type="default" />
            <StatusBox label="Main Power" isActive={mainPower} type="danger" />
          </div>

          {/* Row 3: Triggers */}
          <div className="grid grid-cols-2 gap-6">
            <TriggerButton 
              label="Pump 1 Trigger" 
              isActive={pump1On} 
              onClick={togglePump1} 
            />
            <TriggerButton 
              label="Pump 2 Trigger" 
              isActive={pump2On} 
              onClick={togglePump2} 
            />
          </div>

        </div>
      </div>

      {/* Bottom Row: Water Level Graphs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-48">
        <Card title="Water Level 1">
          <div className="h-full w-full flex flex-col justify-end">
            <div className="text-lg font-bold text-cyan-400 mb-[-10px] ml-2 z-10 relative">
              {Math.round(wl1Data[wl1Data.length-1]?.value || 0)}mm
            </div>
            <div className="flex-1 w-full">
               <MiniGraph data={wl1Data} color="#06b6d4" />
            </div>
          </div>
        </Card>
        <Card title="Water Level 2">
          <div className="h-full w-full flex flex-col justify-end">
            <div className="text-lg font-bold text-cyan-400 mb-[-10px] ml-2 z-10 relative">
              {Math.round(wl2Data[wl2Data.length-1]?.value || 0)}mm
            </div>
            <div className="flex-1 w-full">
               <MiniGraph data={wl2Data} color="#06b6d4" />
            </div>
          </div>
        </Card>
        <Card title="Water Level 3">
          <div className="h-full w-full flex flex-col justify-end">
            <div className="text-lg font-bold text-teal-400 mb-[-10px] ml-2 z-10 relative">
              {Math.round(wl3Data[wl3Data.length-1]?.value || 0)}mm
            </div>
            <div className="flex-1 w-full">
              <MiniGraph data={wl3Data} color="#14b8a6" />
            </div>
          </div>
        </Card>
      </div>

    </div>
  );
}